import { memo, useMemo } from 'react';
import { X } from 'lucide-react';
import { useMap } from 'react-leaflet';
import { formatEventTime } from './MapHelpers';
import { parseLocalDate } from '../../utils/date';
import { safeHref } from '../../utils/url';

const EventPopup = memo(({ event, isPinned, onClose }) => {
  const map = useMap();

  const formattedDate = useMemo(() => {
    const d = parseLocalDate(event.event_date);
    if (!d || Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  }, [event.event_date]);

  const eventUrl = useMemo(() => safeHref(event.event_url), [event.event_url]);

  const formattedTime = useMemo(() =>
    event.event_time ? formatEventTime(event.event_time, event.state) : null,
    [event.event_time, event.state]
  );

  const handleClose = (e) => {
    e.stopPropagation();
    if (onClose) onClose();
    map.closePopup();
  };

  return (
    <div className="p-2" style={{ pointerEvents: 'auto' }}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-lg">📅</span>
          <span className="font-bold text-foreground text-sm">{event.title}</span>
        </div>
        {isPinned && onClose && (
          <button
            onClick={handleClose}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Close"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="text-xs space-y-1 text-muted-foreground">
        {formattedDate && (
          <p className="flex items-center gap-1">
            <span className="font-medium">Date:</span>
            {formattedDate}
            {formattedTime && ` at ${formattedTime}`}
          </p>
        )}

        {event.venue_name && (
          <p className="flex items-center gap-1">
            <span className="font-medium">Venue:</span> {event.venue_name}
          </p>
        )}

        {event.city && (
          <p className="flex items-center gap-1">
            <span className="font-medium">Location:</span> {event.city}{event.state ? `, ${event.state}` : ''}
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          <p className="text-primary text-[10px]">{event.meetup_group_name}</p>
          {eventUrl && (
            <a
              href={eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 text-[10px] text-primary hover:underline"
              style={{ pointerEvents: 'auto' }}
            >
              View Event →
            </a>
          )}
        </div>
      </div>
    </div>
  );
});

export default EventPopup;
